import { BrandMark } from '../components/BrandMark';
import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import { useAgents, useDeleteAgent, useActivateAgent, useDeactivateAgent, useCloneAgent } from '../hooks/useAgents';
import { Agent, AgentType, AgentStatus } from '../types/agent';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { NativeSelect } from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { DeleteConfirmModal, CloneAgentModal, RunAgentModal } from '../components/agent';
import { AgentListSkeleton } from '../components/common/LoadingSkeleton';
import { EmptyState } from '../components/common/EmptyState';
import { Pagination } from '../components/common/Pagination';

export default function AgentList() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<AgentType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<AgentStatus | 'all'>('all');
  const [page, setPage] = useState(1);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    agent: Agent | null;
  }>({ isOpen: false, agent: null });

  // Clone modal state
  const [cloneModal, setCloneModal] = useState<{
    isOpen: boolean;
    agent: Agent | null;
  }>({ isOpen: false, agent: null });

  // Run modal state
  const [runModal, setRunModal] = useState<{
    isOpen: boolean;
    agent: Agent | null;
  }>({ isOpen: false, agent: null });

  // Build query params
  const queryParams = useMemo(() => {
    const params: Record<string, unknown> = { page, limit: 10 };
    if (filterType !== 'all') params.type = filterType;
    if (filterStatus !== 'all') params.status = filterStatus;
    if (searchTerm) params.search = searchTerm;
    return params;
  }, [page, filterType, filterStatus, searchTerm]);

  // Fetch agents
  const { data, isLoading, error } = useAgents(queryParams);
  const deleteAgent = useDeleteAgent();
  const activateAgent = useActivateAgent();
  const deactivateAgent = useDeactivateAgent();
  const cloneAgent = useCloneAgent();

  const agents = data?.data || [];
  const totalPages = data?.meta?.pages || 1;

  const handleDelete = (agent: Agent) => {
    setDeleteModal({ isOpen: true, agent });
  };

  const confirmDelete = async () => {
    if (!deleteModal.agent) return;
    await deleteAgent.mutateAsync(deleteModal.agent.id);
    setDeleteModal({ isOpen: false, agent: null });
  };

  const handleClone = (agent: Agent) => {
    setCloneModal({ isOpen: true, agent });
  };

  const confirmClone = async (newName: string) => {
    if (!cloneModal.agent) return;
    const clonedAgent = await cloneAgent.mutateAsync({
      id: cloneModal.agent.id,
      name: newName,
    });
    setCloneModal({ isOpen: false, agent: null });
    // Navigate to the newly cloned agent's detail page
    navigate(`/agents/${clonedAgent.id}`);
  };

  const handleRun = (agent: Agent) => {
    setRunModal({ isOpen: true, agent });
  };

  const handleToggleStatus = async (agent: Agent) => {
    if (agent.status === 'ACTIVE') {
      await deactivateAgent.mutateAsync(agent.id);
    } else {
      await activateAgent.mutateAsync(agent.id);
    }
  };

  const getStatusBadgeClass = (status: AgentStatus) => {
    const baseClass = 'px-2 py-1 text-xs rounded-full font-medium';
    switch (status) {
      case 'ACTIVE':
        return `${baseClass} bg-green-100 text-green-800`;
      case 'INACTIVE':
        return `${baseClass} bg-gray-100 text-gray-800`;
      case 'PAUSED':
        return `${baseClass} bg-yellow-100 text-yellow-800`;
      case 'ERROR':
        return `${baseClass} bg-red-100 text-red-800`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800`;
    }
  };

  const getTypeIcon = (type: AgentType) => {
    switch (type) {
      case 'CUSTOMER_SUPPORT':
        return '💬';
      case 'DATA_PROCESSING':
        return '📊';
      case 'WORKFLOW_AUTOMATION':
        return '⚙️';
      case 'CONTENT_GENERATION':
        return '✍️';
      case 'ANALYSIS':
        return '🔍';
      case 'INTEGRATION':
        return '🔌';
      case 'CUSTOM':
        return '🔧';
      default:
        return '🤖';
    }
  };

  const hasFilters = searchTerm || filterType !== 'all' || filterStatus !== 'all';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Dashboard
              </Link>
              <Link
                to="/agents"
                className="text-sm font-medium text-foreground"
              >
                Agents
              </Link>
              <Link
                to="/workflows"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Workflows
              </Link>
              <Link
                to="/executions"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Executions
              </Link>
              <Link
                to="/templates"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Marketplace
              </Link>
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            {user && (
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {user.email}
              </span>
            )}
            <UserMenu />
          </div>
        </div>
      </header>

      <div className="p-4">
        <div className="max-w-7xl mx-auto py-8">
          {/* Page Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold">My Agents</h1>
              <p className="text-muted-foreground">
                Manage your AI agents and workflows
              </p>
            </div>
            <Button onClick={() => navigate('/agents/new')}>+ Create Agent</Button>
          </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <NativeSelect
                  id="type"
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value as AgentType | 'all');
                    setPage(1);
                  }}
                >
                  <option value="all">All Types</option>
                  <option value="CUSTOMER_SUPPORT">Customer Support</option>
                  <option value="DATA_PROCESSING">Data Processing</option>
                  <option value="WORKFLOW_AUTOMATION">Workflow Automation</option>
                  <option value="CONTENT_GENERATION">Content Generation</option>
                  <option value="ANALYSIS">Analysis</option>
                  <option value="INTEGRATION">Integration</option>
                  <option value="CUSTOM">Custom</option>
                </NativeSelect>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <NativeSelect
                  id="status"
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value as AgentStatus | 'all');
                    setPage(1);
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="PAUSED">Paused</option>
                  <option value="ERROR">Error</option>
                </NativeSelect>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-4 rounded-md mb-6">
            {error instanceof Error && error.message ? error.message : 'Failed to load agents'}
          </div>
        )}

        {/* Loading State */}
        {isLoading && <AgentListSkeleton count={3} />}

        {/* Empty State */}
        {!isLoading && agents.length === 0 && (
          <EmptyState
            icon="🤖"
            title="No Agents Found"
            description={
              hasFilters
                ? 'Try adjusting your filters to find agents'
                : 'Get started by creating your first agent'
            }
            actionLabel={hasFilters ? undefined : 'Create Your First Agent'}
            onAction={hasFilters ? undefined : () => navigate('/agents/new')}
          />
        )}

        {/* Agent List */}
        {!isLoading && agents.length > 0 && (
          <div className="space-y-4">
            {agents.map((agent) => (
              <Card key={agent.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="text-3xl">{getTypeIcon(agent.type)}</div>
                      <div>
                        <CardTitle
                          className="mb-1 cursor-pointer hover:text-primary"
                          onClick={() => navigate(`/agents/${agent.id}`)}
                        >
                          {agent.name}
                        </CardTitle>
                        <CardDescription>
                          {agent.description || 'No description'}
                        </CardDescription>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className={getStatusBadgeClass(agent.status)}>
                            {agent.status}
                          </span>
                          <span className="px-2 py-1 text-xs rounded-full font-medium bg-blue-100 text-blue-800">
                            {agent.type.replace(/_/g, ' ')}
                          </span>
                        </div>
                        {agent.tags && agent.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {agent.tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-700"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleRun(agent)}
                        disabled={agent.status !== 'ACTIVE'}
                        title={agent.status !== 'ACTIVE' ? 'Agent must be active to run' : 'Run agent'}
                      >
                        Run
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleStatus(agent)}
                        disabled={
                          activateAgent.isPending || deactivateAgent.isPending
                        }
                      >
                        {agent.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/agents/${agent.id}`)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/agents/${agent.id}/edit`)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleClone(agent)}
                        disabled={cloneAgent.isPending}
                      >
                        Clone
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(agent)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              isLoading={isLoading}
            />
          </div>
        )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, agent: null })}
        onConfirm={confirmDelete}
        agentName={deleteModal.agent?.name || ''}
        isDeleting={deleteAgent.isPending}
      />

      {/* Clone Agent Modal */}
      <CloneAgentModal
        isOpen={cloneModal.isOpen}
        onClose={() => setCloneModal({ isOpen: false, agent: null })}
        onConfirm={confirmClone}
        agentName={cloneModal.agent?.name || ''}
        isCloning={cloneAgent.isPending}
      />

      {/* Run Agent Modal */}
      <RunAgentModal
        isOpen={runModal.isOpen}
        onClose={() => setRunModal({ isOpen: false, agent: null })}
        agentId={runModal.agent?.id || ''}
        agentName={runModal.agent?.name || ''}
      />
    </div>
  );
}
